// @ts-strict-ignore
import 'reflect-metadata';
import { Log } from '@shared/debug/log';
import { StringUtils } from '../util/string-utils';

class PropertyData {
  public propertyName: string;
  public type: string;
  public key: string;
  public isCustomConverter: boolean;
}

class ClassData {
  public constr: any;
  public metadata: PropertyData[];
}

let classMetadataStorage: { [beanName: string]: ClassData };
let customConverters: { [beanName: string]: JsonCustomConvert<any> };

export interface JsonCustomConvert<T> {

  /**
   * Converts an incoming JSON object object to a TypeScript object.
   * @param data JSON object
   * @return {any} TypeScript object
   */
  deserialize(data: any): T;
}

export class JsonConvert {

  /**
   * Method that converts object to class.
   * @param data object or array of the objects
   * @param type type of the class or custom converter.
   */
  public static deserialize(data: any, type: string): any {
    if (isStringEmpty(type) || data === undefined || data === null) {
      return data;
    }
    if (isArrayType(type)) {
      return deserializeArray(data, type);
    } else {
      return deserializeObject(data, type);
    }
  }
}

function deserializeArray(data: any, type: string): any {
  if (!(data instanceof Array)) {
    return data;
  }
  const convertedArray: any[] = [];
  if (data.length > 0) {
    const arrayType: string = type.substring(1, type.length - 1);
    for (const arrayEntry of data) {
      const convertedArrayEntry: any = JsonConvert.deserialize(arrayEntry, arrayType);
      if (convertedArrayEntry !== null && convertedArrayEntry !== undefined) {
        convertedArray.push(convertedArrayEntry);
      }
    }
  }
  return convertedArray;
}

function deserializeObject(data: any, type: string): any {
  if (isPrimitive(type)) {
    return deserializePrimitive(data, type);
  } else {
    return deserializeClass(data, type);
  }
}

function deserializePrimitive(data: any, type: string): any {
  switch (String(type).toLowerCase()) {
    case 'number':
      return Number(data);
    case 'string':
      return String(data);
    case 'boolean':
      return Boolean(data);
  }
  return data;
}

function deserializeClass(data: any, type: string): any {
  const classData: ClassData = classMetadataStorage[type];
  if (!classData || !classData.constr) {
    const message: string = 'Error: Convert: class was not found for the type: ' + type;
    Log.error(message);
    throw Error(message);
  }
  const obj: any = new classData.constr();
  if (!classData.metadata) {
    // Support of classes without properties.
    return obj;
  }
  for (const propertyData of classData.metadata) {
    const key: string = propertyData.key;
    const jsonData: any = data[propertyData.propertyName];
    let convertedObject: any;
    if (propertyData.isCustomConverter) {
      const customConverter: JsonCustomConvert<any> = customConverters[propertyData.type];
      if (!customConverter) {
        const message: string = 'Error: Convert: custom converter was not found for the type: ' + propertyData.type;
        Log.error(message);
        throw Error(message);
      }
      convertedObject = customConverter.deserialize(jsonData);
    } else {
      convertedObject = JsonConvert.deserialize(jsonData, propertyData.type);
    }
    if (data !== undefined && data !== null) {
      obj[key] = convertedObject;
    }
  }
  return obj;
}

function isPrimitive(type: string): boolean {
  return 'string' === String(type).toLowerCase()
    || 'number' === String(type).toLowerCase()
    || 'boolean' === String(type).toLowerCase()
    || 'object' === String(type).toLocaleLowerCase();
}

function isArrayType(type: string): boolean {
  if (type.startsWith('[') && type.endsWith(']')) {
    return true;
  }
  return false;
}

function isStringEmpty(str: string): boolean {
  if (!str || str.length === 0) {
    return true;
  }
  return false;
}

/**
 * Classes that use this decorator can be used for the deserialization.
 * @param className the name of the class.
 */
export function JsonObject(className: string) {
  if (isStringEmpty(className)) {
    const message: string = 'Error: Convert: Class name is not valid: ' + className;
    Log.error(message);
    throw Error(message);
  }
  return function (constructor: any) {
    if (!classMetadataStorage) {
      classMetadataStorage = {};
    }
    if (classMetadataStorage.hasOwnProperty(className)) {
      const message: string = 'Error: Convert: class with provided name already exists! ' + className;
      Log.error(message);
      throw Error(message);
    }
    let temporaryBeanName: string;
    for (const key of Object.keys(classMetadataStorage)) {
      if (classMetadataStorage[key].constr === constructor) {
        temporaryBeanName = key;
        break;
      }
    }
    let classData: ClassData;
    if (temporaryBeanName) {
      classData = classMetadataStorage[temporaryBeanName];
      delete classMetadataStorage[temporaryBeanName];
    } else {
      classData = new ClassData();
      classData.constr = constructor;
      classData.metadata = [];
    }
    for (const key of Object.keys(classMetadataStorage)) {
      const jsonObject: any = new constructor();
      if (jsonObject instanceof classMetadataStorage[key].constr) {
        updatePropertyData(classData.metadata, classMetadataStorage[key].metadata);
      }
    }

    classMetadataStorage[className] = classData;
    return constructor;
  };
}

function updatePropertyData(targetData: PropertyData[], sourceData: PropertyData[]): void {
  if (!targetData || !sourceData) {
    return;
  }
  for (const data of sourceData) {
    if (!isPropertyDataExists(data, targetData)) {
      targetData.push(data);
    }
  }
}

function isPropertyDataExists(propertyData: PropertyData, source: PropertyData[]): boolean {
  for (const data of source) {
    if (data.isCustomConverter === propertyData.isCustomConverter && data.key === propertyData.key
      && data.propertyName === propertyData.propertyName && data.type === propertyData.type) {
      return true;
    }
  }
  return false;
}

/**
 * Decorator of the property of @JsonObjectClass
 * @param propertyName the name of the property in json string
 * @param type class name or the custom converter name. For arrays '[String]' syntax should be used.
 * @param isCustomConverter must be set to true if the custom converter is specified.
 */
export function JsonProperty(propertyName: string, type: string, isCustomConverter: boolean = false) {
  if (isStringEmpty(propertyName) || isStringEmpty(type)) {
    const message: string = 'Error: Convert: @JsonProperty contains invalid parameters: ' + propertyName + ' ' + type;
    Log.error(message);
    throw Error(message);
  }
  return function (target: any, key: string) {
    if (!classMetadataStorage) {
      classMetadataStorage = {};
    }
    let classData: ClassData;
    for (const className of Object.keys(classMetadataStorage)) {
      if (target.constructor === classMetadataStorage[className].constr) {
        classData = classMetadataStorage[className];
        break;
      }
    }
    if (!classData) {
      classData = new ClassData();
      classData.constr = target.constructor;
      const temporaryBeanName: string = StringUtils.createUuid();
      classMetadataStorage[temporaryBeanName] = classData;
    }
    const propertyData: PropertyData = new PropertyData();
    propertyData.key = key;
    propertyData.propertyName = propertyName;
    propertyData.type = type;
    propertyData.isCustomConverter = isCustomConverter;
    if (!classData.metadata) {
      classData.metadata = [];
    }
    classData.metadata.push(propertyData);
  };
}

/**
 * Decorator for the custom converter. Custom converter must implement JsonCustomConvert interface.
 * @param converterName name of the class. Is used in the @JsonProperty decorator
 */
export function JsonConverter(converterName: string) {
  if (isStringEmpty(converterName)) {
    const message: string = 'Error: Convert: JsonConverter name is not valid: ' + converterName;
    Log.error(message);
    throw Error(message);
  }
  return function (constructor: any) {
    if (!customConverters) {
      customConverters = {};
    }
    if (customConverters.hasOwnProperty(converterName)) {
      const message: string = 'Error: Convert: class with provided name already exists! ' + converterName;
      Log.error(message);
      throw Error(message);
    }
    customConverters[converterName] = new constructor();
    return constructor;
  };
}
